import { Injectable } from '@angular/core';
import { Observable, filter, map, merge, tap } from 'rxjs';
import { LiabilityLetterDto } from './common/api/models/liabilityLetterDto';
import { InvoicingStatus } from './common/api/models/invoicingStatus';
import { CustomerInfoDto } from './common/api/models/customerInfoDto';
import { LiablePartyDto } from './common/api/models/liabilePartyDto';
import { gcssBookingInfo } from './common/api/models/gcssBookingInfo';
import { CaseDamageDetailDto } from './common/api/models/caseDamageDetailDto';
import { ContainerMoveDto } from './common/api/models/containerMoveDto';
import { SharedCustomerRecoveryCaseService } from './components/customer-recovery/shared-customer-recovery-case.service';
import { CustomerRecoveryCaseDto } from './common/api/models/customerRecoveryCaseDto';
import { FormValidation } from './common/api/models/formValidation';
import { SharedVendorRecoveryCaseService } from './components/vendor-recovery/shared-vendor-recovery-case.service';
import { RecoveryCase } from './common/api/models/recoveryCase';
import { VendorRecoveryCase } from './common/api/models/vendorRecoveryCase';
import { ClaimReferenceDocumentDto } from './common/api/models/claimReferenceDocumentDto';
import { Inspection } from './common/api/models/inspection';
import { TemplateModel } from '@maersk-global/angular-shared-library';
import { InspectionDetailsDTO } from './common/api/models/inspectionDetailsDTO';
import { CaseActivityLogsDtoListResponse } from './common/api/models/caseActivityLogsDtoListResponse';
import { LiabilityLetter } from './common/api/models/liabilityLetter';
import { LiabilityLetterStatus } from './common/api/models/liabilityLetterStatus';
import { SendMailRequest } from './common/api/models/sendMailRequest';
import { InvoiceDetail } from './common/api/models/invoiceDetail';
import { UpdateInvoice } from './common/api/models/updateInvoice';
import { CreateInvoice } from './common/api/models/createInvoice';
import { EmailDetailDto } from './common/api/models/emailDetailDto';
import { LiabilityParty } from './common/api/models/liabilityParty';
import { CreateRecoveryCase } from './common/api/models/createRecoveryCase';
import { CurrencyAndExchangeRateDto } from './common/api/models/currencyAndExchangeRateDto';
import { SharedFeaRecoveryCaseService } from './components/fea-recovery/shared-fea-recovery-case.service';
import { RecoveryCaseOverview } from './common/api/models/recoveryCaseOverview';
import { FeaRecoveryCase } from './common/api/models/feaRecovery';

export interface ISharedRecoveryCaseService {
  reloadRecoveryCaseData(): void;
  resetRecoveryCaseData(): void;
  reloadDamageDetails(): void;
  reloadLiabilityPartyDetails(): void;
  reloadLiabilityLetters(): void;
  reloadInvoices(): void;
  reloadBulkInvoices(): void;
  updateLiabilityDetailsVisibility(value: boolean): void;
  updateIssueInvoiceVisibility(value: boolean): void;
  updateEirImageLastFetchOn(date: Date | undefined): void;
  updateCustomerData(data: CustomerInfoDto): void;
  updateContainerDetails(
    containerDetails: ContainerMoveDto | null | undefined
  ): void;
  updateCurrentStageId(stageId: number): void;
  updateFormValidationState(state: FormValidation): void;
  saveDamageDetails(
    damages: TemplateModel[],
    caseId: number,
    caseCurrency: string
  ): Promise<boolean>;
  caseDocumentGet(caseId: number): Observable<ClaimReferenceDocumentDto | null>;
  caseDocumentPost(
    imagesAssignedToCases: ClaimReferenceDocumentDto
  ): Observable<ClaimReferenceDocumentDto | null>;
  caseDocumentDelete(documentId: number): Observable<boolean>;
  inspectionsGet(
    caseNumber: string,
    containerNumber: string | undefined,
    workOrderNumber: number | undefined,
    incidentDate: Date | null,
    workOrderDate: Date | null,
    fromDateTime?: Date | null,
    toDateTime?: Date | null
  ): Observable<Array<Inspection> | null>;
  saveImageAvailability(
    inspectionInfo: InspectionDetailsDTO,
    caseNumber: string
  ): Observable<boolean>;
  updateOverviewDetails(overview: TemplateModel[]): void;
  getActivityLogs(
    caseId: number,
    useArchive: boolean | undefined
  ): Observable<CaseActivityLogsDtoListResponse>;
  updateLiabilityLetter(
    letter: LiabilityLetter,
    liabilityLetterStatus: LiabilityLetterStatus
  ): Promise<boolean>;
  onResendEmailClicked(letter: SendMailRequest): Observable<boolean>;
  downloadLetter(
    blobPath: string,
    caseNumber: string,
    useArchive: boolean | undefined
  ): Observable<Blob>;
  postInvoiceDetails(
    createInvoice: CreateInvoice,
    userId: string
  ): Observable<boolean>;
  putInvoiceDetails(
    invoiceId: number,
    updateInvoice: UpdateInvoice,
    userId: string
  ): Observable<boolean>;
  saveComment(comment: string): Promise<boolean>;
  saveAttachment(
    caseNumber: string,
    fromDateTime?: Date | null,
    toDateTime?: Date | null
  ): Promise<boolean>;
  updateWorkOrderDate(date: Date | null | undefined): void;
  updateCaseCurrencyAndExchangeRate(
    currencyAndExchangeRate: CurrencyAndExchangeRateDto | undefined
  ): void;
  updateFactCode(factCode: string | undefined): void;
}

@Injectable({
  providedIn: 'root',
})
export class SharedRecoveryCaseService {
  caseContext!: ISharedRecoveryCaseService;

  constructor(
    private _customerRecoverySharedService: SharedCustomerRecoveryCaseService,
    private _vendorRecoverySharedService: SharedVendorRecoveryCaseService,
    private _feaRecoverySharedService: SharedFeaRecoveryCaseService
  ) {}

  allowCustomerContext = () =>
    this.caseContext instanceof SharedCustomerRecoveryCaseService;

  allowVendorContext = () =>
    this.caseContext instanceof SharedVendorRecoveryCaseService;

  allowFeaContext = () =>
    this.caseContext instanceof SharedFeaRecoveryCaseService;

  recoveryCaseData$: Observable<RecoveryCase> = merge(
    this._customerRecoverySharedService.customerRecoveryCaseData$.pipe(
      filter((customerRecovery) => !!customerRecovery),
      tap(() => {
        this.caseContext = this._customerRecoverySharedService;
      }),
      map((customerRecovery) => {
        return this.buildRecoveryCaseModelForCustomer(
          customerRecovery as CustomerRecoveryCaseDto
        );
      })
    ),
    this._vendorRecoverySharedService.vendorRecoveryCaseData$.pipe(
      filter((vendorRecovery) => !!vendorRecovery),
      tap(() => {
        this.caseContext = this._vendorRecoverySharedService;
      }),
      map((vendorRecovery) => {
        return this.buildRecoveryCaseModelForVendor(
          vendorRecovery as VendorRecoveryCase
        );
      })
    ),
    this._feaRecoverySharedService.feaRecoveryCaseData$.pipe(
      filter((feaRecovery) => !!feaRecovery),
      tap(() => {
        this.caseContext = this._feaRecoverySharedService;
      }),
      map((feaRecovery) => {
        return this.buildRecoveryCaseModelForFea(
          feaRecovery as FeaRecoveryCase
        );
      })
    )
  );

  workflowStageId$: Observable<number> = merge(
    this._customerRecoverySharedService.workflowStageId$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.workFlowStageId$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.workFlowStageId$.pipe(
      filter(this.allowFeaContext)
    )
  );

  disableForm$: Observable<boolean> = merge(
    this._customerRecoverySharedService.disableForm$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.disableForm$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.disableForm$.pipe(
      filter(this.allowFeaContext)
    )
  );

  buildRecoveryCaseModelForCustomer(
    customer: CustomerRecoveryCaseDto
  ): RecoveryCase {
    return {
      caseId: customer.caseId,
      recoveryCaseNumber: customer.recoveryCaseNumber,
      workOrderNumber: customer.workOrderNumber,
      containerNumber: customer.containerNumber,
      equipmentNumber: customer.containerNumber,
      containerProdYear: customer.containerProdYear,
      equipmentManufacturingYear: customer.containerProdYear,
      gateInDate: customer.gateInDate,
      bookingNumber: customer.bookingNumber,
      bolNumber: customer.bolNumber,
      shopCode: customer.shopCode,
      shopCountryId: customer.shopCountryId,
      shopCountryCode: customer.shopCountryCode,
      shopCountryName: customer.shopCountryName,
      podCountryId: customer.podCountryId,
      podCountryCode: customer.podCountryCode,
      podCountryName: customer.podCountryName,
      podCountryClusterCode: customer.podCountryClusterCode,
      podCountryClusterName: customer.podCountryClusterName,
      workOrderCostUSD: customer.workOrderCostUSD,
      recoverableCostUSD: customer.recoverableCostUSD,
      withinCoverageCostUSD: customer.withinCoverageCostUSD,
      aboveCoverageCostUSD: customer.aboveCoverageCostUSD,
      recoveryStatusId: customer.recoveryStatusId,
      workOrderStatusCode: customer.workorderStatusCode,
      operatorCode: customer.operatorCode,
      placeOfIncident: customer.placeOfIncident,
      dateOfIncident: customer.dateOfIncident,
      aboveCoverageBand: customer.aboveCoverageBand,
      importReturn: customer.importReturn,
      assignedToUID: customer.assignedToUID,
      cpPurchased: customer.cpPurchased,
      priority: customer.priority,
      isCostChanged: customer.isCostChanged,
      groupId: customer.groupId,
      comment: customer.comments,
      cancellationReason: customer.cancellationReason,
      hasLinkedCases: customer.hasLinkedCases,
      gateInLapseInDays: customer.gateInLapseInDays,
      workOrderDate: customer.workOrderDate,
      invoiceNumber: customer.invoiceNumber,
      workFlowVersion: customer.workFlowVersion,
      templateVersion: customer.templateVersion,
      stopUpgrade: customer.stopUpgrade,
      woMode: customer.woMode,
      podLocalCurrency: customer.podLocalCurrency,
      equipmentSubType: customer.equipmentSubType,
      containerSize: customer.containerSize,
      equipmentSize: customer.containerSize,
      cause: customer.cause,
      equipmentOwnershipType: customer.equipmentOwnershipType,
      completionDate: customer.completionDate,
      vendorReference: customer.vendorReference,
      createdBy: customer.createdBy,
      createdDate: customer.createdDate,
      updatedBy: customer.updatedBy,
      updatedDate: customer.updatedDate,
      locationId: customer.locationId,
      containerEmptyFull: customer.containerEmptyFull,
      siteName: customer.siteName,
      containerCurrentLocation: customer.containerCurrentLocation,
      eirImageLastCheckedOn: customer.eirImageLastCheckedOn,
      caseCurrency: customer.caseCurrency,
      exchangeRateUSDCaseCurrency: customer.exchangeRateUSDCaseCurrency,
      workOrderCostCaseCurrency: customer.workOrderCostCaseCurrency,
      withinCoverageCostCaseCurrency: customer.withinCoverageCostCaseCurrency,
      recoverableCostCaseCurrency: customer.recoverableCostCaseCurrency,
      aboveCoverageCostCaseCurrency: customer.aboveCoverageCostCaseCurrency,
      handlingDamageCostUSD: customer.handlingDamageCostUSD,
      workflowStageId: this._customerRecoverySharedService.getStageId(
        customer.workFlowStage ?? 'New'
      ),
      cpCoverageAmount: customer.cpCoverageAmount,
      cpCoverageCurrency: customer.cpCoverageCurrency,
      cpMasterContainerSize: customer.cpMasterContainerSize,
      isCpActiveCountry: customer.isCpActiveCountry,
      podRegionCode: customer.podRegionCode,
      podRegionName: customer.podRegionName,
      responsibleCountryCode: customer.podCountryCode,
      responsibleCountryClusterCode: customer.podCountryClusterCode,
      responsibleRegionCode: customer.podRegionCode,
      responsibleCountryName: customer.podCountryName,
      responsibleCountryClusterName: customer.podCountryClusterName,
      responsibleRegionName: customer.podRegionName,
      recoveryCaseType: 'Customer',
      attachmentFromDate: customer.attachmentFromDate,
      attachmentToDate: customer.attachmentToDate,
      caseType: customer.caseType,
      archiveStatus: customer.archiveStatus,
    } as RecoveryCase;
  }

  buildCreateRecoveryCaseRequest(
    recoveryCase: RecoveryCase,
    caseType: string
  ): CreateRecoveryCase {
    return {
      equipmentNumber: recoveryCase.equipmentNumber,
      equipmentType: recoveryCase.equipmentSubType,
      equipmentSize: JSON.stringify(recoveryCase.equipmentSize),
      equipmentManufacturingYear: recoveryCase.equipmentManufacturingYear,
      equipmentOwnershipType: recoveryCase.equipmentOwnershipType,
      recoveryCaseType: caseType,
      responsibleCountryCode: recoveryCase.responsibleCountryCode,
      recoverableCostInUsd: recoveryCase.recoverableCostInUsd,
      aboveCoverageCostInUsd: recoveryCase.aboveCoverageCostInUsd,
      caseStatus: 1,
      caseCurrency: recoveryCase.caseCurrency,
      bolNumber: recoveryCase.bolNumber,
      bookingNumber: recoveryCase.bookingNumber,
      invoiceNumber: recoveryCase.invoiceNumber,
      createSource: recoveryCase.createSource,
      assignedTo: recoveryCase.assignedTo,
      workOrderNumber: recoveryCase.workOrderNumber,
      shopCode: recoveryCase.shopCode,
      OperatorCode: recoveryCase.operatorCode,
      workflowStageId: 1,
      workOrderCreatedTimestamp: recoveryCase.workOrderDate,
      isImportReturn: recoveryCase.importReturn,
      workOrderStatusCode: recoveryCase.workOrderStatusCode,
      workOrderMode: recoveryCase.woMode,
    } as CreateRecoveryCase;
  }

  buildRecoveryCaseModelForVendor(vendor: VendorRecoveryCase): RecoveryCase {
    return {
      caseId: vendor.id,
      groupId: vendor.groupId,
      equipmentNumber: vendor.equipmentNumber,
      equipmentType: vendor.equipmentType,
      equipmentSize: vendor.equipmentSize,
      equipmentManufacturingYear: vendor.equipmentManufacturingYear,
      equipmentOwnershipType: vendor.equipmentOwnershipType,
      recoveryCaseType: vendor.recoveryCaseType,
      responsibleCountryCode: vendor.responsibleCountryCode,
      responsibleCountryClusterCode: vendor.responsibleCountryClusterCode,
      responsibleRegionCode: vendor.responsibleRegionCode,
      responsibleCountryName: vendor.responsibleCountryName,
      responsibleCountryClusterName: vendor.responsibleCountryClusterName,
      responsibleRegionName: vendor.responsibleRegionName,
      recoverableCostInUsd: vendor.recoverableCostInUsd,
      aboveCoverageCostInUsd: vendor.aboveCoverageCostInUsd,
      caseStatus: vendor.caseStatus,
      bolNumber: vendor.bolNumber,
      bookingNumber: vendor.bookingNumber,
      invoiceNumber: vendor.invoiceNumber,
      createSource: vendor.createSource,
      assignedTo: vendor.assignedTo,
      createTimestamp: vendor.createdTimestamp,
      createUserId: vendor.createdByUserId,
      workOrderNumber: vendor.workOrderNumber,
      shopCode: vendor.shopCode,
      shopCountryCode: vendor.shopCountryCode,
      containerNumber: vendor.equipmentNumber,
      operatorCode: vendor.operatorCode,
      containerSize: vendor.equipmentSize,
      shopCountryName: vendor.shopCountryName,
      recoverableCostCaseCurrency: vendor.recoverableCostInCaseCurrency,
      recoverableCostUSD: vendor.recoverableCostInUsd,
      workOrderCostUSD: vendor.totalCostInUsd,
      workOrderCostCaseCurrency: vendor.totalCostInCaseCurrency,
      caseCurrency: vendor.caseCurrency,
      workflowStageId: vendor.workflowStageId,
      comment: vendor.comment,
      cancellationReason: vendor.cancellationReason,
      attachmentFromDate: vendor.attachmentFromDate,
      attachmentToDate: vendor.attachmentToDate,
      workOrderDate: vendor.workOrderCreatedTimestamp,
    } as RecoveryCase;
  }

  buildRecoveryCaseModelForFea(feaCase: FeaRecoveryCase): RecoveryCase {
    return {
      caseId: feaCase.id,
      id: feaCase.id,
      groupId: feaCase.groupId,
      equipmentNumber: feaCase.equipmentNumber,
      equipmentType: feaCase.equipmentType,
      equipmentSize: feaCase.equipmentSize,
      equipmentManufacturingYear: feaCase.equipmentManufacturingYear,
      equipmentOwnershipType: feaCase.equipmentOwnershipType,
      recoveryCaseType: feaCase.recoveryCaseType,
      responsibleCountryCode: feaCase.responsibleCountryCode,
      responsibleCountryClusterCode: feaCase.responsibleCountryClusterCode,
      responsibleRegionCode: feaCase.responsibleRegionCode,
      responsibleCountryName: feaCase.responsibleCountryName,
      responsibleCountryClusterName: feaCase.responsibleCountryClusterName,
      responsibleRegionName: feaCase.responsibleRegionName,
      recoverableCostInUsd: feaCase.recoverableCostInUsd,
      aboveCoverageCostInUsd: feaCase.aboveCoverageCostInUsd,
      caseStatus: feaCase.caseStatus,
      bolNumber: feaCase.bolNumber,
      bookingNumber: feaCase.bookingNumber,
      invoiceNumber: feaCase.invoiceNumber,
      createSource: feaCase.createSource,
      assignedTo: feaCase.assignedTo,
      createTimestamp: feaCase.createdTimestamp,
      createUserId: feaCase.createdByUserId,
      workOrderNumber: feaCase.workOrderNumber,
      shopCode: feaCase.shopCode,
      shopCountryCode: feaCase.shopCountryCode,
      containerNumber: feaCase.equipmentNumber,
      operatorCode: feaCase.operatorCode,
      containerSize: feaCase.equipmentSize,
      shopCountryName: feaCase.shopCountryName,
      recoverableCostInCaseCurrency: feaCase.recoverableCostInCaseCurrency,
      recoverableCostUSD: feaCase.recoverableCostInUsd,
      workOrderCostUSD: feaCase.totalCostInUsd,
      workOrderCostCaseCurrency: feaCase.totalCostInCaseCurrency,
      caseCurrency: feaCase.caseCurrency,
      workflowStageId: feaCase.workflowStageId,
      comment: feaCase.comment,
      cancellationReason: feaCase.cancellationReason,
      workOrderDate: feaCase.workOrderCreatedTimestamp,
      toTimestamp: feaCase.toTimestamp,
      fromTimestamp: feaCase.fromTimestamp,
      exchangeRateTimeStamp: feaCase.exchangeRateTimeStamp,
      exchangeRateUSDCaseCurrency:
        feaCase.exchangeRateCaseCurrencyWithRespectToUsd,
      locationId: feaCase.locationId,
      region: feaCase.region,
      billingNumber: feaCase.billingNumber,
      adjustedCost: feaCase.adjustedCost,
      locationName: feaCase.locationName,
      maerskCost: feaCase.maerskCost,
      finalTpPartyCostVendor: feaCase.finalTpPartyCostVendor,
      tpCostVendor: feaCase.tpCostVendor,
      repairCostInCaseCurrency: feaCase.repairCostInCaseCurrency,
      damageCause: feaCase.damageCause,
      agent: feaCase.agent,
      tCostInCaseCurrency: feaCase.tCostInCaseCurrency,
      cpBillingNumber: feaCase.cpBillingNumber,
      aboveCoverageCostCaseCurrency: feaCase.aboveCoverageCostCaseCurrency,
      workOrderStatusCode: feaCase.workOrderStatusCode,
      cpPurchased: feaCase.cpPurchased,
      cpPurchasedBilling: feaCase.cpPurchasedBilling,
      gap: feaCase.gap,
      fileId: feaCase.fileId,
      paymentReceivedAmount: feaCase.paymentReceivedAmount,
    } as RecoveryCase;
  }

  customerData$: Observable<CustomerInfoDto> = merge(
    this._customerRecoverySharedService.customerRecoveryCustomerData$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.vendorRecoveryCustomerData$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.feaRecoveryCustomerData$.pipe(
      filter(this.allowFeaContext)
    )
  );

  currentStageId$: Observable<number> = merge(
    this._customerRecoverySharedService.currentStageId$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.currentStageId$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.currentStageId$.pipe(
      filter(this.allowFeaContext)
    )
  );

  workOrderDate$: Observable<Date | null | undefined> = merge(
    this._customerRecoverySharedService.workOrderDate$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.workOrderDate$.pipe(
      filter(this.allowVendorContext)
    )
  );

  incidentDate$: Observable<Date | null | undefined> = merge(
    this._customerRecoverySharedService.incidentDate$
  );

  eirImageLastFetchDate$: Observable<Date | undefined> = merge(
    this._customerRecoverySharedService.customerRecoveryEirImageLastFetchDate$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.vendorRecoveryEirImageLastFetchDate$.pipe(
      filter(this.allowVendorContext)
    )
  );

  containerDetails$: Observable<ContainerMoveDto | null | undefined> = merge(
    this._customerRecoverySharedService.customerRecoveryContainerMovesDetails$
  );

  LiablePartyData$: Observable<LiablePartyDto | undefined> = merge(
    this._customerRecoverySharedService.customerRecoveryLiablePartyData$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.vendorRecoveryLiablePartyData$.pipe(
      filter(this.allowVendorContext),
      map((party) => {
        if (!party) return undefined;
        return this.buildLiabilityPartyModelForVendor(party as LiabilityParty);
      })
    ),
    this._feaRecoverySharedService.feaRecoveryLiablePartyData$.pipe(
      filter(this.allowFeaContext),
      map((party) => {
        if (!party) return undefined;
        return this.buildLiabilityPartyModelForFea(party as LiabilityParty);
      })
    )
  );

  buildLiabilityPartyModelForVendor(party: LiabilityParty): LiablePartyDto {
    return {
      gcssBookingParty: {
        liablePartyType: party.liablePartyType,
        liablePartyName: party.liablePartyName,
        scvCustomerCode: party.scvCustomerCode,
        factCustomerCode: party.factCustomerCode,
      },
      customBookingParty: {
        scvCustomerCode: party.scvCustomerCode,
        factCustomerCode: party.factCustomerCode,
      },
      customEmailAddress: party.customerEmailAddress,
      placeOfDelivery: party.placeOfDelivery,
      placeOfReceipt: party.placeOfReceipt,
      payers: party.payers,
      billTo: party.billToParty,
      shipTo: party.shipToParty,
      soldTo: party.soldToParty,
      liablePartyLocationCode: party.liablePartyLocationCode,
      liablePartyTypeID: party.liablePartyTypeID,
      liablePartyName: party.liablePartyName,
    };
  }

  buildLiabilityPartyModelForFea(party: LiabilityParty): LiablePartyDto {
    return {
      gcssBookingParty: {
        liablePartyType: party.liablePartyType,
        liablePartyName: party.liablePartyName,
        scvCustomerCode: party.scvCustomerCode,
        factCustomerCode: party.factCustomerCode,
      },
      customBookingParty: {
        scvCustomerCode: party.scvCustomerCode,
        factCustomerCode: party.factCustomerCode,
      },
      customEmailAddress: party.customerEmailAddress,
      placeOfDelivery: party.placeOfDelivery,
      placeOfReceipt: party.placeOfReceipt,
      payers: party.payers,
      billTo: party.billToParty,
      shipTo: party.shipToParty,
      soldTo: party.soldToParty,
      liablePartyTypeID: party.liablePartyTypeID,
      liablePartyName: party.liablePartyName,
      scvCustomerCode: party.scvCustomerCode,
      factCustomerCode: party.factCustomerCode,
    };
  }

  liabilityLetters$: Observable<Array<LiabilityLetter> | undefined> = merge(
    this._customerRecoverySharedService.customerRecoveryLiabilityLetters$.pipe(
      filter(this.allowCustomerContext),
      map((letters) => {
        return letters?.map(
          (letter) =>
            this.buildLetterModelForCustomer(letter) as LiabilityLetter
        );
      })
    ),
    this._vendorRecoverySharedService.vendorRecoveryLiabilityLetters$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.feaRecoveryLiabilityLetters$.pipe(
      filter(this.allowFeaContext)
    )
  );

  buildLetterModelForCustomer(letter: LiabilityLetterDto): LiabilityLetter {
    return {
      id: letter.id,
      recoveryCaseId: letter.recoveryCaseId,
      liablePartyName: letter.liabilityPartyName,
      liabilityAmount: letter.liabilityAmount,
      liabilityLetterStatus: letter.liabilityLetterStatus,
      liabilityLetterCaseType: 'Customer',
      liabilityLetterResponseReason: letter.liabilityLetterResponseReason,
      emailBlobPath: letter.emailBlobPath,
      to: letter.to,
      cc: letter.cc,
      subject: letter.subject,
      body: letter.body,
      emailAttachments: letter.emailAttachment?.fileInfo,
      isManuallyUploaded: false,
      createdByUserId: letter.createdBy,
      updatedByUserId: letter.updatedBy,
      createdTimestamp: letter.createdDate,
      updatedTimestamp: letter.updatedDate,
    } as LiabilityLetter;
  }

  shouldShowLiabilityDetails$: Observable<boolean> = merge(
    this._customerRecoverySharedService.shouldShowCustomerRecoveryLiabilityDetails$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.shouldShowVendorRecoveryLiabilityDetails$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.shouldShowFeaRecoveryLiabilityDetails$.pipe(
      filter(this.allowFeaContext)
    )
  );

  invoices$: Observable<Array<InvoiceDetail> | undefined> = merge(
    this._vendorRecoverySharedService.vendorRecoveryInvoices$.pipe(
      filter(this.allowVendorContext)
    ),
    this._customerRecoverySharedService.customerRecoveryInvoices$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._feaRecoverySharedService.feaRecoveryInvoices$.pipe(
      filter(this.allowFeaContext)
    )
  );

  shouldShowIssueInvoiceScreen$: Observable<boolean> = merge(
    this._customerRecoverySharedService.shouldShowCustomerRecoveryIssueInvoiceScreen$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.shouldShowVendorRecoveryIssueInvoiceScreen$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.shouldShowFeaRecoveryIssueInvoiceScreen$.pipe(
      filter(this.allowFeaContext)
    )
  );

  invoiceCreationPermission$: Observable<
    InvoicingStatus & { displayMessage: string }
  > = merge(
    this._customerRecoverySharedService.customerRecoveryInvoiceCreationPermission$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.vendorRecoveryInvoiceCreationPermission$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.feaRecoveryInvoiceCreationPermission$.pipe(
      filter(this.allowFeaContext)
    )
  );

  bookingCargoDetails$: Observable<gcssBookingInfo | undefined> = merge(
    this._customerRecoverySharedService.customerRecoveryBookingCargoDetails$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.VendorRecoveryBookingCargoDetails$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.FeaRecoveryBookingCargoDetails$.pipe(
      filter(this.allowFeaContext)
    )
  );

  damageDetails$: Observable<CaseDamageDetailDto[] | undefined> = merge(
    this._customerRecoverySharedService.damageDetails$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.damageDetails$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.damageDetails$.pipe(
      filter(this.allowFeaContext)
    )
  );

  overviewDetails$: Observable<TemplateModel[] | undefined> = merge(
    this._customerRecoverySharedService.overviewDetails$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.overviewDetails$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.overviewDetails$.pipe(
      filter(this.allowFeaContext)
    )
  );

  recoveryCaseOverview$: Observable<RecoveryCaseOverview | undefined> = merge(
    this._feaRecoverySharedService.recoveryCaseOverview$.pipe(
      filter(this.allowFeaContext)
    )
  );

  selectedFactCode$: Observable<string | undefined> = merge(
    this._feaRecoverySharedService.selectedFactCode$.pipe(
      filter(this.allowFeaContext)
    )
  );

  vendorNotificationEmailAttributes$: Observable<EmailDetailDto> =
    this.recoveryCaseData$.pipe(
      map((caseData) => ({
        from: 'noreply-vendor-image-request@maersk.com',
        to: 'dummy_vendor@email.com',
        cc: 'dummy_vendor_carbon_copy@email.com',
        bcc: 'dummy_vendor_blind_carbon_copy@email.com',
        type: 'vendor',
        caseNumber: caseData.recoveryCaseNumber ?? caseData.caseId?.toString(),
        workOrderNumber: caseData.workOrderNumber,
        shopCode: caseData.shopCode,
      }))
    );

  caseCurrencyAndExchangeRate$: Observable<
    CurrencyAndExchangeRateDto | undefined
  > = merge(
    this._customerRecoverySharedService.caseCurrencyAndExchangeRate$.pipe(
      filter(this.allowCustomerContext)
    ),
    this._vendorRecoverySharedService.caseCurrencyAndExchangeRate$.pipe(
      filter(this.allowVendorContext)
    ),
    this._feaRecoverySharedService.caseCurrencyAndExchangeRate$.pipe(
      filter(this.allowFeaContext)
    )
  );

  saveDamageDetails = (
    damages: TemplateModel[],
    caseId: number,
    caseCurrency: string
  ) => this.caseContext?.saveDamageDetails(damages, caseId, caseCurrency);

  updateLiabilityLetter = (
    letter: LiabilityLetter,
    liabilityLetterStatus: LiabilityLetterStatus
  ) => this.caseContext.updateLiabilityLetter(letter, liabilityLetterStatus);

  onResendEmailClicked = (letter: SendMailRequest) =>
    this.caseContext.onResendEmailClicked(letter);

  downloadLetter = (
    blobPath: string,
    caseNumber: string,
    useArchive: boolean | undefined
  ) => this.caseContext.downloadLetter(blobPath, caseNumber, useArchive);

  reloadRecoveryCaseData = () => this.caseContext?.reloadRecoveryCaseData();
  resetRecoveryCaseData = () => this.caseContext?.resetRecoveryCaseData();
  reloadDamageDetails = () => this.caseContext?.reloadDamageDetails();

  reloadLiabilityPartyDetails = () =>
    this.caseContext?.reloadLiabilityPartyDetails();

  reloadLiabilityLetters = () => this.caseContext?.reloadLiabilityLetters();

  reloadInvoices = () => this.caseContext?.reloadInvoices();

  reloadBulkInvoices = () => this.caseContext?.reloadBulkInvoices();

  updateLiabilityDetailsVisibility = (value: boolean) =>
    this.caseContext?.updateLiabilityDetailsVisibility(value);

  updateIssueInvoiceVisibility = (value: boolean) =>
    this.caseContext?.updateIssueInvoiceVisibility(value);

  updateEirImageLastFetchOn = (date: Date | undefined) =>
    this.caseContext?.updateEirImageLastFetchOn(date);

  updateCustomerData = (data: CustomerInfoDto) =>
    this.caseContext?.updateCustomerData(data);

  updateContainerDetails = (
    containerDetails: ContainerMoveDto | null | undefined
  ) => this.caseContext?.updateContainerDetails(containerDetails);

  updateCurrentStageId = (stageId: number) =>
    this.caseContext?.updateCurrentStageId(stageId);

  updateFormValidationState = (state: FormValidation) =>
    this.caseContext?.updateFormValidationState(state);

  caseDocumentGet = (caseId: number) =>
    this.caseContext?.caseDocumentGet(caseId);

  caseDocumentPost = (imagesAssignedToCases: ClaimReferenceDocumentDto) =>
    this.caseContext?.caseDocumentPost(imagesAssignedToCases);

  caseDocumentDelete = (documentId: number) =>
    this.caseContext?.caseDocumentDelete(documentId);

  inspectionsGet = (
    caseNumber: string,
    containerNumber: string | undefined,
    workOrderNumber: number | undefined,
    incidentDate: Date | null,
    workOrderDate: Date | null,
    fromDateTime?: Date | null,
    toDateTime?: Date | null
  ) =>
    this.caseContext?.inspectionsGet(
      caseNumber,
      containerNumber,
      workOrderNumber,
      incidentDate,
      workOrderDate,
      fromDateTime,
      toDateTime
    );

  saveImageAvailability = (
    inspectionInfo: InspectionDetailsDTO,
    caseNumber: string
  ) => this.caseContext?.saveImageAvailability(inspectionInfo, caseNumber);

  updateOverviewDetails = (overview: TemplateModel[]) =>
    this.caseContext.updateOverviewDetails(overview);

  getActivityLogs = (
    caseId: number,
    useArchive: boolean | undefined = undefined
  ) => this.caseContext?.getActivityLogs(caseId, useArchive);

  postInvoiceDetails = (createInvoice: CreateInvoice, userId: string) =>
    this.caseContext.postInvoiceDetails(createInvoice, userId);

  putInvoiceDetails = (
    invoiceId: number,
    updateInvoice: UpdateInvoice,
    userId: string
  ) => this.caseContext.putInvoiceDetails(invoiceId, updateInvoice, userId);

  saveComment = (comment: string) => this.caseContext.saveComment(comment);

  saveAttachment = (
    caseNumber: string,
    fromDateTime?: Date,
    toDateTime?: Date
  ) => this.caseContext.saveAttachment(caseNumber, fromDateTime, toDateTime);

  updateWorkOrderDate = (date: Date | null | undefined) =>
    this.caseContext.updateWorkOrderDate(date);

  updateCaseCurrencyAndExchangeRate = (
    currencyAndExchangeRate: CurrencyAndExchangeRateDto | undefined
  ) =>
    this.caseContext.updateCaseCurrencyAndExchangeRate(currencyAndExchangeRate);

  updateFactCode = (factCode: string | undefined) => {
    if (this.allowFeaContext()) {
      this._feaRecoverySharedService.updateFactCode(factCode);
    }
  };
}
